import { buildColors } from '@t4s/ui/tokens'

/**
 * What became of a tenant's stored brand colour, in the three words the console uses for it.
 *
 * - `as-supplied` — readable, and the light scheme draws the tenant's primary fill as the colour it
 *   stored;
 * - `corrected` — readable, but `buildColors` had to **move the fill itself** on the light scheme to
 *   carry white text at 4.5:1 (docs/ux/PLATFORM-ADMIN.md §13.2). `brand.corrected` means exactly
 *   that, since #319: `solid` differs from the supplied colour, compared as normalised hex;
 * - `fallback` — not readable as a colour at all, so the platform's default is what the tenant's
 *   apps draw.
 *
 * **Why the light scheme alone decides the word.** The dark scheme's fill is corrected for the
 * opposite reason (ink text, so it has to be *lighter*), and the two corrections cannot both be
 * avoided, and that is arithmetic, not a sample. The light scheme writes white on a fill
 * (luminance 1), so a fill is unmoved there only if L <= 1.05/4.5 - 0.05 = ~0.1833; the dark scheme
 * writes `#12171d` (L = ~0.0083), so a fill is unmoved there only if L >= 4.5 * (0.0083 + 0.05) -
 * 0.05 = ~0.2124. The two ranges are disjoint, so no colour is unmoved in both (pinned in
 * `brand-colour.test.ts` against the real `buildColors`, with every grey as a check). Reading
 * "moved in either scheme" would therefore say Corrected of every tenant, which is
 * the bug #319 fixed (WORKPLAN Chores `fix-ui-brand-corrected-flag-always-true`). So the badge is
 * about the scheme the console draws its own swatch in, and the dark scheme's own movement is still
 * said, per scheme, in the preview card's swatch captions and panes. `packages/ui` owns the
 * correction; the stored value is never changed by it (§13.2).
 *
 * Both screens call `buildColors` with the stored string and read `corrected` and `fallback` off the
 * result; this function is that, and nothing here knows how a correction works. There is no test
 * seam: every state is reachable with the real function.
 */
export type BrandColourState = 'as-supplied' | 'corrected' | 'fallback'

export type BrandColour = {
  readonly state: BrandColourState
  /**
   * The colour a tenant's primary button actually is on the light scheme — the stored value
   * where it was usable, the corrected one where it was not, the platform default for a
   * fallback. This is what a swatch should show, never the raw stored string.
   */
  readonly swatch: string
}

export function brandColourState(brand: string): BrandColour {
  const light = buildColors('light', brand)

  const state: BrandColourState = light.brand.fallback
    ? 'fallback'
    : light.brand.corrected
      ? 'corrected'
      : 'as-supplied'

  return { state, swatch: light.brand.solid }
}

/**
 * How each state is worded and drawn wherever the console names it: the Overview's Branding card
 * and the Branding screen's preview card. One table, so the two cannot say different things.
 *
 * Corrected is the **warning** badge: since #319 it is true only when the tenant's colour really
 * is not the colour its buttons are drawn in, which is what §13.2 meant to warn about.
 */
export const COLOUR_BADGE: Record<
  BrandColourState,
  { readonly label: string; readonly className: string; readonly meaning: string }
> = {
  'as-supplied': {
    label: 'As supplied',
    className: 'badge badge-live',
    meaning: 'The light scheme draws it as stored. The dark scheme may still lighten it.',
  },
  corrected: {
    label: 'Corrected',
    className: 'badge badge-warning',
    meaning:
      'Adjusted on the light scheme so text on it stays legible. The stored value is unchanged.',
  },
  fallback: {
    label: 'Fallback',
    className: 'badge badge-danger',
    meaning: 'Could not be read as a colour, so the platform default is shown.',
  },
}

/**
 * A colour as a CSS value a swatch can be drawn with.
 *
 * `buildColors` accepts three- and six-digit hex **with or without the `#`** and hands back the
 * string it was given when nothing needed correcting; `ff0000` is a colour to it and not to CSS. A
 * swatch needs the `#`. Only a string `buildColors` has already read as a colour is passed here, so
 * what comes out is always a hex colour and never arbitrary text.
 */
export function cssColour(value: string): string {
  return value.startsWith('#') ? value : `#${value}`
}

/**
 * What the colour picker should show: a `#rrggbb` value, always, because `<input type="color">`
 * accepts nothing else and a value it cannot parse is silently replaced by black.
 *
 * **There is no second colour parser here.** Whether a string is a colour is decided by
 * `brandColourState` — `buildColors`' own parse, the same gate the swatch and the preview use —
 * and a string it reads is, by that parser's grammar, three or six hex digits with an optional
 * `#`; all that is left is spelling it the way the picker wants (`#` first, six digits, lower
 * case). In order: the colour in the text field if it is readable, else the stored colour if that
 * is, else the platform default's fill (what the tenant's apps would draw). It never throws, and a
 * blank, half-typed or unreadable value leaves the picker where it was most recently useful rather
 * than on black.
 */
export function pickerColour(typed: string, stored: string): string {
  const candidates = [typed.trim(), stored.trim()]
  const readable = candidates.find(
    (candidate) => candidate !== '' && brandColourState(candidate).state !== 'fallback',
  )
  if (readable === undefined) {
    return brandColourState('').swatch.toLowerCase()
  }
  const digits = readable.replace('#', '').toLowerCase()
  return `#${digits.length === 3 ? digits.replace(/./g, (digit) => digit + digit) : digits}`
}
